 
import React from 'react';
import { useForm } from 'react-hook-form';
import loadable from '@loadable/component';
import { parse } from 'date-fns';
import { dateObjToDateOnlyISOString } from '@cp/shared/lib';
import { AppFormProvider } from '@cp/utils/form';
import { sleep } from '@cp/utils/sleep';
import { useIsMobile } from '@cp/ds/src/hooks';
import { FormAvailableOnlyFieldName } from 'src/entities/catalog/ui/availableOnly/model';
import { CatalogSortingTypes } from 'src/entities/catalog/model/types';
import { getDefaultValues } from '../model/utils/getDefaultValues';
import { FormFieldName, FormSubmitTrigger } from '../model/form';
import { WatcherProps } from '../model/watcher.interface';
import { DATE_VALUE_FORMAT } from '../model/const';
import { Watcher } from '../model/watcher';
import { CatalogNavResumeFormModel } from '../model';
import { CatalogNavProps, CatalogNavViewProps } from './model';

const ViewDesktop = loadable(() => import('./view.desktop'));
const ViewMobile = loadable(() => import('./view.mobile'));

export const CatalogNav: React.FC<CatalogNavProps> = ({
  careerId,
  catalogType,
  sx,
  citySearchFormField,
  onChange,
  onChangeCity,
  onChangeSorting,
  onMapButtonClick,
  defaultCity,
  defaultFilter,
  defaultSorting,
  careerDictionary,
}) => {
  const isMobile = useIsMobile();
  const defaultValues = React.useMemo(
    () => getDefaultValues(defaultCity, defaultFilter ?? {}, defaultSorting),
    [defaultCity, defaultFilter, defaultSorting],
  );

  const CATALOG_NAV = [ViewDesktop, ViewMobile];

  const form = useForm<CatalogNavResumeFormModel>({
    defaultValues,
  });

  const submit = React.useCallback<FormSubmitTrigger>(async () => {
    await sleep(0);
    const values = form.getValues();

    if (onChange) {
      const scheduleDate = values[FormAvailableOnlyFieldName.Date]
        ? dateObjToDateOnlyISOString(parse(values[FormAvailableOnlyFieldName.Date] as string, DATE_VALUE_FORMAT, new Date()))
        : undefined;
      const scheduleTimeFrom = values[FormAvailableOnlyFieldName.Start] ?? undefined;
      const scheduleTimeTo = values[FormAvailableOnlyFieldName.End] ?? undefined;

      onChange({
        date: scheduleDate,
        start: scheduleTimeFrom,
        end: scheduleTimeTo,
      });
    }

    if (onChangeSorting) {
      const sorting = values[FormFieldName.Sorters] || CatalogSortingTypes.Normal;
      onChangeSorting(sorting);
    }
  }, [form, onChange, onChangeSorting]);

  const watcherProps: WatcherProps = { defaultCity, form, onChangeCity, submit, defaultValues };

  const viewProps: CatalogNavViewProps = {
    careerId,
    catalogType,
    form,
    submit,
    onMapButtonClick,
    sx,
    careerDictionary,
    citySearchFormField,
  };

  const View = CATALOG_NAV[Number(isMobile)];

  return (
    <AppFormProvider form={form}>
      <Watcher {...watcherProps} />
      <View {...viewProps} />
    </AppFormProvider>
  );
};
